import React from 'react';
import { Link, StyleSheet, Text, View } from '@react-pdf/renderer';
import { parseAsteriskBold } from './emphasis';

// Shared react-pdf HTML-rendering pipeline, extracted from OnePagerPdf.tsx (the group
// one-pager template) so other PDF templates (e.g. interim) get the same hard-won
// handling of Quill output — asterisk-bold, underline quirks, block/inline splitting —
// without re-deriving it. OnePagerPdf.tsx keeps its own copy untouched to avoid any risk
// to the already-shipped group PDF; this module is for new templates going forward.

export const richTextStyles = StyleSheet.create({
  h1: {
    fontSize: 18,
    fontWeight: 700,
    color: '#111827',
    marginBottom: 6,
  },
  h2: {
    fontSize: 13,
    fontWeight: 700,
    color: '#1F2937',
    marginBottom: 8,
  },
  h3: {
    fontSize: 12,
    fontWeight: 700,
    color: '#1F2937',
    marginBottom: 6,
  },
  h4: {
    fontSize: 11,
    fontWeight: 700,
    color: '#1F2937',
    marginBottom: 6,
  },
  link: {
    color: '#155dfc',
    textDecoration: 'underline',
  },
  paragraph: {
    fontSize: 11,
    lineHeight: 1.35,
    marginBottom: 6,
  },
  bulletRow: {
    flexDirection: 'row',
    marginBottom: 6,
  },
  bulletDot: {
    width: 10,
    fontSize: 11,
  },
  bulletText: {
    flex: 1,
    fontSize: 11,
    lineHeight: 1.35,
  },
});

type InlineStyle = {
  fontWeight?: number;
  fontStyle?: 'italic';
  textDecoration?: 'underline' | 'line-through' | 'none';
  textDecorationStyle?: 'solid' | 'double' | 'dotted' | 'dashed' | 'wavy';
  textDecorationColor?: string;
  borderBottomWidth?: number;
  borderBottomColor?: string;
  borderBottomStyle?: 'solid';
};

export const renderAsteriskBoldPdf = (input: string, keyPrefix: string): React.ReactNode[] => {
  const tokens = parseAsteriskBold(input ?? '');
  return tokens
    .map((t, idx) => {
      if (!t.value) return null;
      if (t.type === 'bold') {
        return (
          <Text key={`${keyPrefix}-b-${idx}`} style={{ fontWeight: 700 }}>
            {t.value}
          </Text>
        );
      }
      return <Text key={`${keyPrefix}-t-${idx}`}>{t.value}</Text>;
    })
    .filter((n): n is JSX.Element => n !== null);
};

const normalizeHtml = (raw: string) => {
  const trimmed = (raw ?? '').trim();
  if (!trimmed) return '';
  // If the string looks double-encoded (plain text with escaped HTML tags),
  // decode entities so DOMParser receives valid HTML
  if (trimmed.includes('&lt;') || trimmed.includes('&gt;')) {
    return trimmed
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");
  }
  return trimmed;
};

const mergeInlineStyle = (base: InlineStyle, next: InlineStyle): InlineStyle => {
  const result: InlineStyle = {};
  const fw = next.fontWeight ?? base.fontWeight; if (fw != null) result.fontWeight = fw;
  const fs = next.fontStyle ?? base.fontStyle; if (fs != null) result.fontStyle = fs;
  const td = next.textDecoration ?? base.textDecoration; if (td != null) result.textDecoration = td;
  const tds = next.textDecorationStyle ?? base.textDecorationStyle; if (tds != null) result.textDecorationStyle = tds;
  const tdc = next.textDecorationColor ?? base.textDecorationColor; if (tdc != null) result.textDecorationColor = tdc;
  const bbw = next.borderBottomWidth ?? base.borderBottomWidth; if (bbw != null) result.borderBottomWidth = bbw;
  const bbc = next.borderBottomColor ?? base.borderBottomColor; if (bbc != null) result.borderBottomColor = bbc;
  const bbs = next.borderBottomStyle ?? base.borderBottomStyle; if (bbs != null) result.borderBottomStyle = bbs;
  return result;
};

const computeInlineStyleForTag = (tagName: string): InlineStyle => {
  const t = tagName.toLowerCase();
  if (t === 'strong' || t === 'b') return { fontWeight: 700 };
  if (t === 'em' || t === 'i') return { fontStyle: 'italic' };
  if (t === 'u')
    return {
      textDecoration: 'underline',
      textDecorationStyle: 'solid',
      textDecorationColor: '#111827',
      borderBottomWidth: 0.75,
      borderBottomColor: '#111827',
      borderBottomStyle: 'solid',
    };
  return {};
};

const computeInlineStyleForElement = (el: Element): InlineStyle => {
  const tagStyle = computeInlineStyleForTag(el.tagName);

  const className = (el.getAttribute('class') ?? '').toLowerCase();
  const styleAttr = (el.getAttribute('style') ?? '').toLowerCase();

  const inline: InlineStyle = {};

  // Quill often uses spans with inline styles/classes instead of <u>
  if (className.includes('ql-underline')) {
    inline.textDecoration = 'underline';
    inline.textDecorationStyle = 'solid';
    inline.textDecorationColor = '#111827';
    inline.borderBottomWidth = 0.75;
    inline.borderBottomColor = '#111827';
    inline.borderBottomStyle = 'solid';
  }
  if (className.includes('ql-bold')) inline.fontWeight = 700;
  if (className.includes('ql-italic')) inline.fontStyle = 'italic';

  if (styleAttr.includes('text-decoration') && styleAttr.includes('underline')) {
    inline.textDecoration = 'underline';
    inline.textDecorationStyle = 'solid';
    inline.textDecorationColor = '#111827';
    inline.borderBottomWidth = 0.75;
    inline.borderBottomColor = '#111827';
    inline.borderBottomStyle = 'solid';
  }
  if (styleAttr.includes('font-style') && styleAttr.includes('italic')) inline.fontStyle = 'italic';
  if (styleAttr.includes('font-weight')) {
    if (styleAttr.includes('bold')) inline.fontWeight = 700;
    const m = styleAttr.match(/font-weight\s*:\s*(\d+)/);
    if (m?.[1]) {
      const n = Number(m[1]);
      if (!Number.isNaN(n)) inline.fontWeight = n;
    }
  }

  return mergeInlineStyle(tagStyle, inline);
};

const getTextFromNode = (node: Node): string => {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? '';
  if (node.nodeType !== Node.ELEMENT_NODE) return '';
  const el = node as Element;
  if (el.tagName.toLowerCase() === 'br') return '\n';
  return Array.from(el.childNodes)
    .map((n) => getTextFromNode(n))
    .join('');
};

const getDirectListItems = (listEl: Element): Element[] => {
  const out: Element[] = [];
  Array.from(listEl.querySelectorAll('li')).forEach((li) => {
    if (li.parentElement === listEl) out.push(li);
  });
  return out;
};

const renderInlineNodes = (nodes: Node[], style: InlineStyle, keyPrefix: string): React.ReactNode[] => {
  const out: React.ReactNode[] = [];
  nodes.forEach((node, idx) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent ?? '';
      if (text) {
        const tokens = parseAsteriskBold(text);
        tokens.forEach((t, tIdx) => {
          const value = t.value;
          if (!value) return;
          if (t.type === 'bold') {
            out.push(
              <Text key={`${keyPrefix}-t-${idx}-${tIdx}`} style={mergeInlineStyle(style, { fontWeight: 700 })}>
                {value}
              </Text>
            );
          } else if (style.fontStyle != null || style.fontWeight != null || style.textDecoration != null || style.borderBottomWidth != null) {
            // Active inline style — wrap in Text to apply it
            out.push(
              <Text key={`${keyPrefix}-t-${idx}-${tIdx}`} style={style}>
                {value}
              </Text>
            );
          } else {
            // No active style — push raw string so react-pdf renders it inline alongside
            // styled siblings. When every child is a <Text> node react-pdf v4 stacks them
            // as blocks; mixing raw strings triggers the correct inline flow.
            out.push(value);
          }
        });
      }
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as Element;
    const tag = el.tagName.toLowerCase();

    const elementStyle = computeInlineStyleForElement(el);
    const nextStyle = mergeInlineStyle(style, elementStyle);

    // react-pdf underline can be unreliable when applied to nested <Text> nodes.
    // If an element results in an underline style, render it as a single leaf run
    // with the fully merged style (so bold+italic+underline compose).
    if ((nextStyle.textDecoration === 'underline' || nextStyle.borderBottomWidth) && tag !== 'a' && tag !== 'br') {
      const text = getTextFromNode(el);
      if (text) {
        out.push(
          <Text key={`${keyPrefix}-u-${idx}`} style={nextStyle}>
            {text}
          </Text>
        );
      }
      return;
    }

    if (tag === 'a') {
      const href = el.getAttribute('href') ?? '';
      const children = renderInlineNodes(Array.from(el.childNodes), nextStyle, `${keyPrefix}-a-${idx}`);
      if (!href) {
        out.push(
          <Text key={`${keyPrefix}-a-${idx}`} style={nextStyle}>
            {children}
          </Text>
        );
        return;
      }

      out.push(
        <Link key={`${keyPrefix}-a-${idx}`} src={href} style={{ ...(nextStyle as object), ...(richTextStyles.link as object) }}>
          {children}
        </Link>
      );
      return;
    }

    if (tag === 'br') {
      out.push(
        <Text key={`${keyPrefix}-br-${idx}`} style={nextStyle}>
          {'\n'}
        </Text>
      );
      return;
    }

    // Preserve nested markup (don't flatten to plain text), so combined formatting survives.
    // Block-level elements are handled by the top-level renderer; if we encounter them here,
    // treat them as containers.
    if (tag === 'p' || tag === 'div' || tag === 'li' || tag === 'ul' || tag === 'ol') {
      out.push(
        <Text key={`${keyPrefix}-blk-${idx}`} style={nextStyle}>
          {renderInlineNodes(Array.from(el.childNodes), nextStyle, `${keyPrefix}-blk-${idx}`)}
        </Text>
      );
      return;
    }

    // Avoid wrapping inline elements in an extra <Text> box — react-pdf treats each nested
    // <Text> as a separate inline run, and the redundant wrapper introduces whitespace
    // artifacts around formatted text. Push the children directly with the merged style.
    out.push(...renderInlineNodes(Array.from(el.childNodes), nextStyle, `${keyPrefix}-${idx}`));
  });
  return out;
};

export const renderRichHtml = (html: string, keyPrefix = 'rt'): React.ReactNode[] => {
  const normalized = normalizeHtml(html);
  if (!normalized) return [];

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(normalized, 'text/html');
    const body = doc.body;

    // Block-level tags that should each become their own block in the output.
    // Anything else (em, strong, span, a, br, etc.) is treated as inline and
    // grouped with adjacent text nodes into a single paragraph.
    const BLOCK_LEVEL_TAGS = new Set(['p', 'div', 'ul', 'ol', 'section', 'article', 'blockquote', 'header', 'footer', 'main', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'pre', 'table']);
    const blocks: Element[] = [];
    let pendingInline: Node[] = [];
    const flushPending = () => {
      if (pendingInline.length === 0) return;
      const p = doc.createElement('p');
      pendingInline.forEach((n) => p.appendChild(n.cloneNode(true)));
      blocks.push(p);
      pendingInline = [];
    };
    Array.from(body.childNodes).forEach((n) => {
      if (n.nodeType === Node.ELEMENT_NODE) {
        const tag = (n as Element).tagName.toLowerCase();
        if (BLOCK_LEVEL_TAGS.has(tag)) {
          flushPending();
          blocks.push(n as Element);
        } else {
          pendingInline.push(n);
        }
      } else if (n.nodeType === Node.TEXT_NODE) {
        if ((n.textContent ?? '').trim()) pendingInline.push(n);
      }
    });
    flushPending();

    const out: React.ReactNode[] = [];
    blocks.forEach((el, idx) => {
      const tag = el.tagName.toLowerCase();

      if (tag === 'ul' || tag === 'ol') {
        const lis = getDirectListItems(el);
        let orderedCount = 0;

        lis.forEach((li, liIdx) => {
          const isOrdered = tag === 'ol' || li.getAttribute('data-list') === 'ordered';
          if (isOrdered) orderedCount += 1;
          const marker = isOrdered ? `${orderedCount}. ` : '• ';

          out.push(
            <View key={`${keyPrefix}-li-${idx}-${liIdx}`} style={richTextStyles.bulletRow}>
              <Text style={richTextStyles.bulletDot}>{marker}</Text>
              <Text style={richTextStyles.bulletText}>
                {renderInlineNodes(Array.from(li.childNodes), {}, `${keyPrefix}-li-${idx}-${liIdx}`)}
              </Text>
            </View>
          );
        });
        return;
      }

      if (tag === 'h1') {
        out.push(
          <View key={`${keyPrefix}-h1-${idx}`}>
            <Text style={richTextStyles.h1}>{renderInlineNodes(Array.from(el.childNodes), {}, `${keyPrefix}-h1-${idx}`)}</Text>
          </View>
        );
        return;
      }

      if (tag === 'h2') {
        out.push(
          <View key={`${keyPrefix}-h2-${idx}`}>
            <Text style={richTextStyles.h2}>{renderInlineNodes(Array.from(el.childNodes), {}, `${keyPrefix}-h2-${idx}`)}</Text>
          </View>
        );
        return;
      }

      if (tag === 'h3') {
        out.push(
          <View key={`${keyPrefix}-h3-${idx}`}>
            <Text style={richTextStyles.h3}>{renderInlineNodes(Array.from(el.childNodes), {}, `${keyPrefix}-h3-${idx}`)}</Text>
          </View>
        );
        return;
      }

      if (tag === 'h4' || tag === 'h5' || tag === 'h6') {
        out.push(
          <View key={`${keyPrefix}-h4-${idx}`}>
            <Text style={richTextStyles.h4}>{renderInlineNodes(Array.from(el.childNodes), {}, `${keyPrefix}-h4-${idx}`)}</Text>
          </View>
        );
        return;
      }

      // For block elements (p, div, etc.) process their children as inline content.
      // For inline elements that appear at the top level without a block wrapper
      // (e.g. AI-generated content), pass the element itself so its style is preserved.
      const BLOCK_TAGS = new Set(['p', 'div', 'section', 'article', 'blockquote', 'header', 'footer', 'main']);
      const nodes = BLOCK_TAGS.has(tag) ? Array.from(el.childNodes) : [el as Node];
      out.push(
        <View key={`${keyPrefix}-p-${idx}`}>
          <Text style={richTextStyles.paragraph}>{renderInlineNodes(nodes, {}, `${keyPrefix}-p-${idx}`)}</Text>
        </View>
      );
    });

    return out;
  } catch {
    const stripped = html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    return [
      <View key={`${keyPrefix}-fallback`}>
        <Text style={richTextStyles.paragraph}>{stripped}</Text>
      </View>,
    ];
  }
};
